import React, { useRef, useState, useEffect, useMemo } from 'react'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { ProcedureAttachment } from '@dataTypes/claimFeature.types'
import { getFileIcon } from '@utils/file.utils'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { v4 as uuid } from 'uuid'
import FileViewer from '../FileViewerModal/FileViewer.component'
import {
  attachmentTypeOptions,
  updateAndCompleteAttachmentsData,
} from '@utils/claims.util'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { useFileSelectionForClaimProcedure } from '@context/FileSelectionForClaimProcedureContext'

const formatOptions = ['PDF', 'JPG', 'PNG', 'DOCX', 'TXT']

type ClaimProcedureAttachmentProps = {
  attachments: ProcedureAttachment[]
  onUpdateAttachments: (updatedAttachments: ProcedureAttachment[]) => void
  editMode: boolean
}

const ClaimProcedureAttachment: React.FC<ClaimProcedureAttachmentProps> = ({
  attachments,
  onUpdateAttachments,
  editMode,
}) => {
  const [procedureAttachments, setProcedureAttachments] =
    useState<ProcedureAttachment[]>(attachments)
  const [editingItemNo, setEditingItemNo] = useState<string | null>(null)
  const [editedAttachment, setEditedAttachment] = useState<
    Partial<ProcedureAttachment>
  >({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileViewerInfo, setFileViewerInfo] = useState<{
    title: string
    show: boolean
    file: File
    url: string
  }>({
    title: '',
    show: false,
    file: {} as File,
    url: '',
  })
  const { openForClaimProcedure } = useFileSelectionForClaimProcedure()

  useEffect(() => {
    setProcedureAttachments(attachments)
  }, [attachments])

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (!event.target.files) return

    const uploadedFiles = Array.from(event.target.files).map((file, index) => ({
      itemNo: `CPA-${procedureAttachments.length + index + 1}`,
      item: file.name,
      formatType: file.type.split('/')[1]?.toUpperCase() || 'UNKNOWN',
      orientation: 'Portrait',
      dateTaken: new Date().toISOString().split('T')[0],
      fileName: file.name.replace(/\s+/g, '_'),
      mimeType: file.type,
      size: file.size,
      isEditing: false,
      notes: [],
      newFile: file,
      mapId: uuid(),
    }))

    const updatedAttachments = [...procedureAttachments, ...uploadedFiles]
    setProcedureAttachments(updatedAttachments)
    onUpdateAttachments(updatedAttachments)
    event.target.value = ''
  }

  const handleChange = (
    field: keyof ProcedureAttachment,
    value: string,
    itemNo?: string,
  ): void => {
    setEditedAttachment((prev) => ({ ...prev, [field]: value }))
    const updatedAttachments = procedureAttachments.map((att) =>
      att.itemNo === itemNo ? { ...att, [field]: value } : att,
    )

    setProcedureAttachments(updatedAttachments)
    onUpdateAttachments(updatedAttachments)
  }

  const handleSave = (): void => {
    const updatedAttachments = procedureAttachments.map((att) =>
      att.itemNo === editingItemNo ? { ...att, ...editedAttachment } : att,
    )

    setProcedureAttachments(updatedAttachments)
    onUpdateAttachments(updatedAttachments)
    setEditingItemNo(null)
  }

  const handleDelete = (itemNo: string): void => {
    const updatedAttachments = procedureAttachments.filter(
      (att) => att.itemNo !== itemNo,
    )
    setProcedureAttachments(updatedAttachments)
    onUpdateAttachments(updatedAttachments)
  }

  const handleViewAttachment = (attachment: ProcedureAttachment): void => {
    setFileViewerInfo({
      title: `Viewing: ${attachment.item}`,
      show: true,
      file: attachment.newFile!,
      url: attachment.fileUrl!,
    })
  }

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString()

  const columns = useMemo(
    () => [
      { field: 'itemNo', headerText: 'Item #', width: '70px' },
      {
        field: 'item',
        headerText: 'Attachment',
        width: '70px',
        template: (rowData: ProcedureAttachment): JSX.Element => (
          <div
            className="file-icon-wrapper"
            title={rowData.item}
            onClick={() => handleViewAttachment(rowData)}
            style={{ cursor: 'pointer' }}
          >
            <i className={`${getFileIcon(rowData.item)} file-icon fs-2`} />
          </div>
        ),
      },
      {
        field: 'formatType',
        headerText: 'Format type',
        width: '100px',
        template: (rowData: ProcedureAttachment): JSX.Element =>
          editingItemNo === rowData.itemNo ? (
            <select
              value={editedAttachment.formatType || rowData.formatType}
              onChange={(e) => handleChange('formatType', e.target.value)}
              className="form-select"
            >
              {formatOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <span>{rowData.formatType.toString().toUpperCase()}</span>
          ),
      },
      {
        field: 'attachmentType',
        headerText: 'Attachment type',
        width: '200px',
        template: (rowData: ProcedureAttachment): JSX.Element =>
          editMode === true ? (
            <select
              value={rowData.attachmentType}
              onChange={(e) =>
                handleChange('attachmentType', e.target.value, rowData.itemNo)
              }
              className="form-select"
            >
              <option value="">Select attachment type</option>
              {attachmentTypeOptions
                .filter((option) =>
                  ['03', 'RB', 'P6', 'OZ'].includes(option.value),
                )
                .sort(
                  (a, b) =>
                    ['03', 'RB', 'P6', 'OZ'].indexOf(a.value) -
                    ['03', 'RB', 'P6', 'OZ'].indexOf(b.value),
                )
                .map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>
          ) : (
            <span>
              {attachmentTypeOptions.find(
                (option) => option.value === rowData.attachmentType,
              )?.label || 'N/A'}
            </span>
          ),
      },
      {
        field: 'dateTaken',
        headerText: 'Date taken',
        width: '80px',
        template: (rowData: ProcedureAttachment): JSX.Element => (
          <span>{formatDate(rowData.dateTaken)}</span>
        ),
      },
      {
        field: 'actions',
        headerText: 'Actions',
        width: '70px',
        template: (rowData: ProcedureAttachment): JSX.Element => (
          <div className="action-buttons-container">
            {editingItemNo === rowData.itemNo && (
              <button
                type="button"
                title="Save"
                className="action-button"
                onClick={handleSave}
              >
                <i className="ti ti-check text-success" />
              </button>
            )}
            <ActionButtons
              onView={() => handleViewAttachment(rowData)}
              onDelete={() => handleDelete(rowData.itemNo)}
              showView={true}
              showDelete={editMode === true}
              showMoreOptions={false}
            />
          </div>
        ),
      },
    ],
    [procedureAttachments, editingItemNo, editedAttachment],
  )

  return (
    <div>
      <div className="text-end my-2">
        <input
          type="file"
          multiple
          ref={fileInputRef}
          className="hidden-file-input"
          onChange={handleFileUpload}
          accept=".pdf,.png,.jpg,.jpeg,.gif"
          style={{ display: 'none' }}
        />

        {editMode && (
          <DropDownButtonComponent
            items={[
              {
                text: 'Local device',
                iconCss: 'mgc_device_line',
              },
              {
                text: 'Cloud storage',
                iconCss: 'mgc_cloud_line',
              },
            ]}
            cssClass="upload-source-dropdown e-caret-hide act-button"
            select={(args) => {
              if (args.item.text === 'Local device') {
                fileInputRef.current?.click()
              } else if (args.item.text === 'Cloud storage') {
                openForClaimProcedure(async (selectedAttachments) => {
                  const updatedAttachments =
                    await updateAndCompleteAttachmentsData(selectedAttachments)
                  const updated = [
                    ...procedureAttachments,
                    ...updatedAttachments,
                  ] as ProcedureAttachment[]
                  setProcedureAttachments(updated)
                  onUpdateAttachments(updated)
                }, procedureAttachments.length)
              }
            }}
          >
            <i className="ti ti-upload me-1"></i>{' '}
            <span
              className="upload-source-text"
              style={{ fontSize: '0.875rem' }}
            >
              Upload
            </span>
          </DropDownButtonComponent>
        )}
      </div>

      <DataGridComponent
        key={procedureAttachments.length}
        data={procedureAttachments}
        columns={columns}
        customCSS="claim-attachment-table"
        showCheckbox={false}
      />

      {fileViewerInfo.show && (
        <FileViewer
          show={fileViewerInfo.show}
          onHide={() => setFileViewerInfo({ ...fileViewerInfo, show: false })}
          title={fileViewerInfo.title}
          {...(fileViewerInfo.file && { sourcefile: fileViewerInfo.file })}
          {...(fileViewerInfo.url && { sourceUrl: fileViewerInfo.url })}
        />
      )}
    </div>
  )
}

export default ClaimProcedureAttachment
